import type {
  EditableComponent,
  EditableComponentsMap,
  OneOfPromptOption,
  PromptOptionValue,
  SelectedPromptOptionValues,
  UnselectableEditableComponentsMap,
} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {MoodType} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import type {PromptOption} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {
  getIconPropsForPromptOptionValue,
  getEditableComponentCheckboxContent as getBaseEditableComponentCheckboxContent,
  getPromptOptionEnum,
  getPromptOptionTitle,
  getPromptOptionValueContent,
} from '@Libraries/ai-redesign-prompt-box-library';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import styles from './ai-redesign-contextual-menu.module.scss';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {Tooltip} from 'react-bootstrap';

interface AiRedesignContextualMenuProps {
  promptOptionsToUse: PromptOption[];
  selectedPromptOptionValues: SelectedPromptOptionValues;
  onSelectedPromptOptionValuesChange: (promptOptionValues: SelectedPromptOptionValues) => void;
  contextualMenuWidth: number;
  editableComponentsMap: EditableComponentsMap;
  onEditableComponentsMapChange: (editableComponentsMap: EditableComponentsMap) => void;
  unselectableEditableComponents?: UnselectableEditableComponentsMap;
  moodType?: MoodType;
}

export function AIRedesignContextualMenu({selectedPromptOptionValues, editableComponentsMap, ...props}: AiRedesignContextualMenuProps): ReactElement {
  const generatePromptOptionValueKey = (option: PromptOption, optionValue: PromptOptionValue): string => {
    return `${optionValue}-${option}-list-item`;
  };

  const handleSelectedPromptOptionValueChange = (changedProperty: OneOfPromptOption): void => {
    const {option, value} = changedProperty;

    const updatedSelectedPromptOptionValues = {
      ...selectedPromptOptionValues,
      [option]: value,
    };

    props.onSelectedPromptOptionValuesChange(updatedSelectedPromptOptionValues);
  };

  const getSelectorHeading = (promptOption: PromptOption): ReactElement => {
    const title = getPromptOptionTitle(promptOption, props.moodType ?? MoodType.REDESIGN);
    return <Text bold val={title} size={TextSize.XXSMALL} className={styles.contextualMenuHeading} />;
  };

  const getPillButton = (optionValue: PromptOptionValue, promptOption: PromptOption, selectedValueKey: string): ReactElement => {
    const key = generatePromptOptionValueKey(promptOption, optionValue);
    const text = getPromptOptionValueContent(optionValue);
    const icon = getIconPropsForPromptOptionValue(optionValue);

    return (
      <PillButton
        key={key}
        text={text}
        isSelected={key === selectedValueKey}
        size={PillButtonSize.SMALL}
        leftIconProps={icon}
        onClick={() => {
          const changedProperty = {
            option: promptOption,
            value: optionValue,
          };

          handleSelectedPromptOptionValueChange(changedProperty);
        }}
        textSize={TextSize.XXSMALL}
      />
    );
  };

  const getPromptOptionValues = (option: PromptOption): PromptOptionValue[] => {
    return Object.values(getPromptOptionEnum(option, props.moodType)).map((enumMember: PromptOptionValue) => {
      return enumMember;
    });
  };

  const getOptionValues = (promptOption: PromptOption): ReactElement => {
    const optionValues = getPromptOptionValues(promptOption);
    const selectedValue = selectedPromptOptionValues[promptOption];
    const selectedValueKey = generatePromptOptionValueKey(promptOption, selectedValue);

    return <>{optionValues.map((optionValue) => getPillButton(optionValue, promptOption, selectedValueKey))}</>;
  };

  const getPromptOptionValueSelector = (promptOption: PromptOption): ReactElement => {
    return (
      <div className={'flexbox flex-column gap-2'}>
        {getSelectorHeading(promptOption)}
        <div className={styles.contextualMenuPillContent}>{getOptionValues(promptOption)}</div>
      </div>
    );
  };

  const getPromptOptionValueSelectors = (): ReactElement => {
    return (
      <>
        {props.promptOptionsToUse.map((option) => {
          return <div key={option}>{getPromptOptionValueSelector(option)}</div>;
        })}
      </>
    );
  };

  const getEditableComponentCheckboxes = (editableComponentsMap: EditableComponentsMap): ReactElement => {
    const editableComponents = Object.keys(editableComponentsMap);

    const isLastSelectableComponent = (component: EditableComponent): boolean => {
      const isCurrentlyChecked = editableComponentsMap[component];

      if (!isCurrentlyChecked) {
        return false;
      }

      const otherCheckedComponents = editableComponents.filter((otherComponent) => {
        const comp = otherComponent as EditableComponent;
        return comp !== component && editableComponentsMap[comp];
      });

      return otherCheckedComponents.length === 0;
    };

    const noOpChange = (): void => {
      return;
    };

    return (
      <div className={styles.contextualMenuContainer}>
        {editableComponents.map((editableComponent) => {
          const component = editableComponent as EditableComponent;
          const baseFilter = getBaseEditableComponentCheckboxContent(component, editableComponentsMap, styles.checkboxIcon, styles.checkboxLabel);
          const isComponentEnabled = editableComponentsMap[component] ?? false;
          const tooltipText = props.unselectableEditableComponents?.[component];
          const isUnselectable = props.unselectableEditableComponents && component in props.unselectableEditableComponents;

          if (isUnselectable && tooltipText) {
            return (
              <div key={component}>
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="top"
                  delay={{show: 50, hide: 100}}
                  overlay={
                    <Tooltip id={`tooltip-${component}`} className="spacing-m-b-3 spacing-m-l-1">
                      <Text size={TextSize.XSMALL} val={tooltipText} />
                    </Tooltip>
                  }
                >
                  <div>
                    <LabelledCheckbox
                      {...baseFilter}
                      isDisabled={true}
                      isChecked={isComponentEnabled}
                      onChange={noOpChange}
                      textClassName={`${baseFilter.textClassName} content-sub-text`}
                      labelIconClassName={`${baseFilter.labelIconClassName} content-sub-text`}
                    />
                  </div>
                </OverlayTrigger>
              </div>
            );
          }

          if (isUnselectable) {
            return (
              <div key={component}>
                <LabelledCheckbox
                  {...baseFilter}
                  isDisabled={true}
                  isChecked={isComponentEnabled}
                  onChange={noOpChange}
                  textClassName={`${baseFilter.textClassName} content-sub-text`}
                  labelIconClassName={`${baseFilter.labelIconClassName} content-sub-text`}
                />
              </div>
            );
          }

          return (
            <div key={component}>
              <LabelledCheckbox
                {...baseFilter}
                onChange={(newCheckedState: boolean) => {
                  if (!newCheckedState && isLastSelectableComponent(component)) {
                    return;
                  }
                  const newState = {
                    ...editableComponentsMap,
                    [component]: newCheckedState,
                  };
                  props.onEditableComponentsMapChange(newState);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const getEditableComponentSelector = (): ReactElement | null => {
    if (Object.keys(editableComponentsMap).length <= 1) {
      return null;
    }

    return (
      <div>
        <Text bold val={window.i18next.t('pmwjs_content_to_update')} size={TextSize.XXSMALL} className={styles.contextualMenuHeading} />
        {getEditableComponentCheckboxes(editableComponentsMap)}
      </div>
    );
  };

  const getContextualMenu = (): ReactElement => {
    return (
      <div
        style={{maxWidth: props.contextualMenuWidth > 0 ? props.contextualMenuWidth : '100%'}}
        className={'flexbox flex-column gap-4 spacing-p-t-3 spacing-p-r-4 spacing-p-b-5 spacing-p-l-4'}
      >
        {getEditableComponentSelector()}
        {getPromptOptionValueSelectors()}
      </div>
    );
  };

  return getContextualMenu();
}
